import { Component, ElementRef, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Organization, OrgPart } from '@newgenus/common';

import { DialogService } from '../../../../../services/dialog.service';
import { OrgBoardService } from '../../../services/org-board-service';
import {
    BuilderConfig,
    BuilderSavePayload,
    BuilderTreeNode,
    buildBuilderNodeFromOrgPart,
    extractIndexFromPath,
} from '../../../shared/org-part-tree-builder/builder-models';
import { OrgPartTreeBuilderComponent } from '../../../shared/org-part-tree-builder/org-part-tree-builder.component';

export interface EditPositionDialogData {
    part: OrgPart;
    partTypes: string[];
    isDeveloper?: boolean;
    organization?: Organization;
    orgKey?: string;
    /** Open with a new Board of Directors seat pre-added (board box "Add board seat"). */
    seedBoardSeat?: { connectsTo?: string | null };
}

/** Allowed child types per parent type — mirrors backend validation. */
const ALLOWED_CHILDREN: Record<string, string[]> = {
    executive: ['executive', 'division', 'post'],
    division: ['department', 'post'],
    department: ['section', 'post'],
    section: ['post', 'unit'],
    unit: ['post'],
    post: [],
};

const DEFAULT_COLOUR = { background: '#000000', color: '#ffffff', linkColor: '#000000' };

/**
 * Edit-Position dialog wrapper. All UI + behaviour now lives in
 * `OrgPartTreeBuilderComponent`; this wrapper is responsible for:
 *   - injecting `MAT_DIALOG_DATA` and translating it into a single-root
 *     `BuilderTreeNode` tree,
 *   - building the breadcrumb from the organization's part graph,
 *   - composing the `BuilderConfig` (single-root mode, protectedRoot, edit-only
 *     `preSave` warning, `saveCommand` closure over `obService.bulkSave`,
 *     `refreshTree` for Continue Editing),
 *   - closing the MatDialog when the user cancels or finishes.
 *
 * External call sites (`organization-board.component.ts`,
 * `org-position-details-dialog`, `org-positions-list-dialog`) are unchanged —
 * they still call `dialog.open(OrgEditPositionDialogComponent, { data: { part,
 * organization, ... } })`.
 */
@Component({
    selector: 'shared-org-edit-position-dialog',
    imports: [OrgPartTreeBuilderComponent],
    template: `
        @if (initialTree.length > 0) {
            <lib-org-part-tree-builder
                [initialTree]="initialTree"
                [config]="config"
                [partTypes]="partTypes"
                [isDeveloper]="isDeveloper"
                (closeRequested)="onClose($event)"
            >
            </lib-org-part-tree-builder>
        }
    `,
})
export class OrgEditPositionDialogComponent implements OnInit {
    initialTree: BuilderTreeNode[] = [];
    config!: BuilderConfig;
    partTypes: string[];
    isDeveloper: boolean;

    constructor(
        public dialogRef: MatDialogRef<OrgEditPositionDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: EditPositionDialogData,
        private obService: OrgBoardService,
        private dialogService: DialogService,
        private hostEl: ElementRef<HTMLElement>
    ) {
        this.isDeveloper = data.isDeveloper || false;
        this.partTypes = data.partTypes || ['executive', 'division', 'department', 'section', 'unit', 'post'];
        // Full-bleed on phones (DESIGN.md §Sheets, OE-160); phone-scoped in
        // global-styles.scss, desktop untouched.
        this.dialogRef.addPanelClass('ng-fullbleed-dialog');
    }

    ngOnInit(): void {
        const rootNode = this.buildInitialTree();
        if (!rootNode) {
            // Defensive: data.part missing → close immediately.
            this.dialogRef.close();
            return;
        }
        rootNode.collapsed = false;
        this.initialTree = [rootNode];
        this.config = this.buildConfig(rootNode);
    }

    // ─────────────────────────────────────────────────────────────
    // Tree + breadcrumb construction
    // ─────────────────────────────────────────────────────────────

    private buildInitialTree(): BuilderTreeNode | null {
        if (!this.data.part) return null;
        const orgParts = this.data.organization?.orgParts || [];
        return buildBuilderNodeFromOrgPart(this.data.part, orgParts, 0, extractIndexFromPath, DEFAULT_COLOUR, false);
    }

    private buildBreadcrumb(): { name: string; partType: string }[] {
        const orgParts = this.data.organization?.orgParts || [];
        const trail: { name: string; partType: string }[] = [];
        let current: OrgPart | undefined = orgParts.find((p) => p.partKey === this.data.part.parentPartKey);
        while (current) {
            trail.unshift({ name: current.name, partType: current.partType || '' });
            current = orgParts.find((p) => p.partKey === current!.parentPartKey);
        }
        // Append the part itself as the current breadcrumb item.
        trail.push({ name: this.data.part.name || 'Untitled', partType: this.data.part.partType || '' });
        return trail;
    }

    // ─────────────────────────────────────────────────────────────
    // Config
    // ─────────────────────────────────────────────────────────────

    private buildConfig(rootNode: BuilderTreeNode): BuilderConfig {
        const totalDescendants = this.countDescendants(rootNode);
        const headerSubtitle =
            totalDescendants > 0 ? `${totalDescendants} descendant${totalDescendants !== 1 ? 's' : ''}` : '';

        return {
            mode: 'edit',
            minDepth: 1,
            protectedRootPartKey: rootNode.partKey,
            showAddRootButton: false,
            maxPositions: undefined,
            getAllowedChildTypes: (parentType: string) => ALLOWED_CHILDREN[parentType] || ['post'],
            defaultColour: { ...rootNode.colour },
            hideSubtitleForNewNodes: false,
            anchorPartKey: rootNode.partKey,
            insertionType: 'child',
            breadcrumb: this.buildBreadcrumb(),
            showInsertionPointChip: false,
            headerSubtitle,
            headerIcon: 'account_tree',
            headerTitle: 'Edit Org Part',
            saveButtonLabel: 'Apply Changes',
            showDeveloperInfo: this.isDeveloper,
            organization: this.data.organization!,
            seedBoardSeat: this.data.seedBoardSeat,
            preSave: () => this.preSaveCheck(),
            saveCommand: (payload) => this.saveCommand(payload),
            refreshTree: (collapsedKeys) => this.refreshTree(collapsedKeys),
        };
    }

    private countDescendants(node: BuilderTreeNode): number {
        let count = 0;
        const visit = (n: BuilderTreeNode) => {
            for (const c of n.children) {
                count++;
                visit(c);
            }
        };
        visit(node);
        return count;
    }

    // ─────────────────────────────────────────────────────────────
    // Hooks
    // ─────────────────────────────────────────────────────────────

    /**
     * Pre-save check: warn the user when changing a post to a container type
     * (which orphans pending hat assignments). Returns false to abort save.
     */
    private async preSaveCheck(): Promise<boolean> {
        const builder = this.findBuilder();
        if (!builder) return true;

        const orgHats = (this.data.organization as any)?.orgHats || {};
        const orgUsers = (this.data.organization as any)?.orgUsers || {};

        const affected: BuilderTreeNode[] = [];
        const visit = (n: BuilderTreeNode) => {
            if (n.dirty && !n.isNew && n._original.partType === 'post' && n.partType !== 'post') {
                affected.push(n);
            }
            n.children.forEach(visit);
        };
        builder.tree.forEach(visit);

        if (affected.length === 0) return true;

        const lines: string[] = [];
        for (const node of affected) {
            const assignedNames: string[] = [];
            const pendingNames: string[] = [];
            let hatCount = 0;

            for (const hatKey of Object.keys(orgHats)) {
                const hat = orgHats[hatKey];
                if (hat?.positionArray?.includes(node.partKey)) {
                    hatCount++;
                    for (const uid of hat.assignedToArray || []) {
                        assignedNames.push(orgUsers[uid]?.name || orgUsers[uid]?.userName || uid);
                    }
                    for (const uid of hat.pendingAssignedToArray || []) {
                        pendingNames.push(orgUsers[uid]?.name || orgUsers[uid]?.userName || uid);
                    }
                }
            }

            if (hatCount > 0 || assignedNames.length > 0 || pendingNames.length > 0) {
                const details: string[] = [];
                if (assignedNames.length > 0) details.push(`Assigned (kept): ${assignedNames.join(', ')}`);
                if (pendingNames.length > 0) details.push(`Pending (removed): ${pendingNames.join(', ')}`);
                if (hatCount > 0) details.push(`${hatCount} hat(s)`);
                lines.push(`• "${node.name}": ${details.join(' · ')}`);
            }
        }
        if (lines.length === 0) return true;

        const message = `The following post(s) are being changed to a container type. Pending approvals will be cancelled, but assigned users will keep their hats:\n\n${lines.join('\n')}`;
        return await this.dialogService.asyncConfirm('Post Type Change Warning', message);
    }

    private async saveCommand(payload: BuilderSavePayload): Promise<void> {
        const orgKey = this.data.organization?.key || this.data.orgKey;
        if (!orgKey) throw new Error('Edit-Position dialog: organization key is required');
        // Strip the two builder-only fields before forwarding to the service.
        const { expectedNewHatCount: _enh, reorderOnlySave: _ros, ...servicePayload } = payload;
        await this.obService.bulkSave(orgKey, servicePayload);
    }

    /**
     * Continue-Editing refresh: re-read the current organization (the realtime
     * listener will have applied backend updates by now), find the same root
     * part, and produce a fresh tree. Collapse-state restore is handled inside
     * the shared component via the supplied `collapsedKeys` map.
     */
    private async refreshTree(_collapsedKeys: Map<string, boolean>): Promise<{
        initialTree: BuilderTreeNode[];
        breadcrumb?: { name: string; partType: string }[];
    } | null> {
        const freshOrg = this.obService.organization;
        if (freshOrg) this.data.organization = freshOrg;

        const orgParts = this.data.organization?.orgParts || [];
        const freshRootPart = orgParts.find((p) => p.partKey === this.data.part.partKey);
        if (!freshRootPart) return null;
        this.data.part = freshRootPart;

        const rootNode = buildBuilderNodeFromOrgPart(
            freshRootPart,
            orgParts,
            0,
            extractIndexFromPath,
            DEFAULT_COLOUR,
            false
        );
        rootNode.collapsed = false;

        return {
            initialTree: [rootNode],
            breadcrumb: this.buildBreadcrumb(),
        };
    }

    // ─────────────────────────────────────────────────────────────
    // Builder lookup (for the preSave hook)
    // ─────────────────────────────────────────────────────────────

    /**
     * Locate the rendered `OrgPartTreeBuilderComponent` instance via the host
     * DOM. Avoids @ViewChild + ngAfterViewInit plumbing for a hook that's only
     * called once per save attempt.
     */
    private findBuilder(): OrgPartTreeBuilderComponent | null {
        const el = this.hostEl.nativeElement.querySelector('lib-org-part-tree-builder');
        if (!el) return null;
        const ctx = (el as any).__ngContext__ as unknown[] | undefined;
        if (!Array.isArray(ctx)) return null;
        for (const item of ctx) {
            if (item && typeof item === 'object' && (item as any).constructor?.name === 'OrgPartTreeBuilderComponent') {
                return item as OrgPartTreeBuilderComponent;
            }
        }
        return null;
    }

    // ─────────────────────────────────────────────────────────────
    // Close
    // ─────────────────────────────────────────────────────────────

    onClose(event: { saved: boolean }): void {
        this.dialogRef.close(event);
    }
}
